import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, websocketUrl } from "./api";
import { loadSnapshot, saveSnapshot } from "./session";
import type { Command, GameView, Session } from "./types";

type Connection = "verbinden" | "online" | "offline" | "opnieuw_verbinden";

interface QueuedAction {
  key: string;
  expectedVersion: number;
  command: Command;
}

function queueKey(gameId: string): string {
  return `vorstenduel.queue.${gameId}`;
}

function readQueue(gameId: string): QueuedAction[] {
  try {
    return JSON.parse(localStorage.getItem(queueKey(gameId)) || "[]");
  } catch {
    return [];
  }
}

function writeQueue(gameId: string, queue: QueuedAction[]): void {
  localStorage.setItem(queueKey(gameId), JSON.stringify(queue));
}

export function useGame(session: Session) {
  const cached = loadSnapshot(session.gameId);
  const [game, setGame] = useState<GameView | undefined>(cached?.game);
  const gameRef = useRef(game);
  const [connection, setConnection] = useState<Connection>(navigator.onLine ? "verbinden" : "offline");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [pendingOffline, setPendingOffline] = useState(readQueue(session.gameId).length);
  const reconnectTimer = useRef<number | undefined>(undefined);
  const socketRef = useRef<WebSocket | undefined>(undefined);

  useEffect(() => {
    gameRef.current = game;
    if (game) saveSnapshot(session.gameId, game);
  }, [game, session.gameId]);

  const refresh = useCallback(async () => {
    try {
      const latest = await api.state(session);
      setGame(latest);
      setError(undefined);
      return latest;
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "offline") setConnection("offline");
      setError(cause instanceof Error ? cause.message : "Synchroniseren mislukt");
      throw cause;
    }
  }, [session]);

  const replayQueue = useCallback(async () => {
    const queue = readQueue(session.gameId);
    if (!queue.length) return;
    let latest: GameView | undefined;
    for (const pending of queue) {
      try {
        // Always retry with the original idempotency key. If the request did
        // reach the server before the connection dropped, the server returns
        // that exact committed revision without applying it twice.
        latest = await api.action(
          session,
          pending.expectedVersion,
          pending.key,
          pending.command,
        );
      } catch (cause) {
        if (!(cause instanceof ApiError) || cause.code !== "version_conflict") throw cause;
        latest = await api.state(session);
        writeQueue(session.gameId, []);
        setPendingOffline(0);
        setGame(latest);
        setError("Een offline actie botste met een nieuwere spelstand en is veilig niet uitgevoerd.");
        return;
      }
      writeQueue(session.gameId, readQueue(session.gameId).filter((item) => item.key !== pending.key));
    }
    setPendingOffline(0);
    // A later opponent action may already exist after our idempotent retry.
    // Finish with the authoritative head revision instead of a stale receipt.
    setGame(await api.state(session));
  }, [session]);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const connect = () => {
      if (cancelled || !navigator.onLine) return;
      setConnection(attempt ? "opnieuw_verbinden" : "verbinden");
      const socket = new WebSocket(websocketUrl(session), ["catan-duel", session.token]);
      socketRef.current = socket;
      socket.onopen = async () => {
        attempt = 0;
        setConnection("online");
        try {
          await replayQueue();
          await refresh();
        } catch {
          // The status and error are already exposed to the player.
        }
      };
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === "snapshot") setGame(message.state);
          if (message.type === "changed" && message.version !== gameRef.current?.version) void refresh();
        } catch {
          setError("De realtime melding was onleesbaar; de spelstand wordt opnieuw geladen.");
          void refresh();
        }
      };
      socket.onclose = () => {
        if (cancelled) return;
        setConnection(navigator.onLine ? "opnieuw_verbinden" : "offline");
        attempt += 1;
        reconnectTimer.current = window.setTimeout(connect, Math.min(15000, 800 * 2 ** Math.min(attempt, 4)));
      };
      socket.onerror = () => socket.close();
    };
    const online = () => {
      window.clearTimeout(reconnectTimer.current);
      connect();
    };
    const offline = () => setConnection("offline");
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    connect();
    return () => {
      cancelled = true;
      window.clearTimeout(reconnectTimer.current);
      socketRef.current?.close();
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, [refresh, replayQueue, session]);

  const command = useCallback(
    async (value: Command) => {
      const current = gameRef.current;
      if (!current || busy) return;
      if (readQueue(session.gameId).length) {
        setError(
          "Wacht tot de reeds bewaarde offline actie met de server is gesynchroniseerd.",
        );
        return;
      }
      const pending: QueuedAction = {
        key: crypto.randomUUID(),
        expectedVersion: current.version,
        command: value,
      };
      setBusy(true);
      setError(undefined);
      try {
        const next = await api.action(session, pending.expectedVersion, pending.key, value);
        setGame(next);
      } catch (cause) {
        if (cause instanceof ApiError && cause.code === "offline") {
          const queue = readQueue(session.gameId);
          if (!queue.some((item) => item.expectedVersion === pending.expectedVersion)) {
            queue.push(pending);
            writeQueue(session.gameId, queue);
            setPendingOffline(queue.length);
          }
          setConnection("offline");
          setError("Actie veilig bewaard. Deze wordt bij dezelfde spelrevisie opnieuw aangeboden zodra de verbinding terug is.");
        } else if (cause instanceof ApiError && cause.code === "version_conflict") {
          setError("De andere speler was je net voor. De actuele spelstand is geladen.");
          await refresh().catch(() => undefined);
        } else {
          setError(cause instanceof Error ? cause.message : "Actie mislukt");
        }
      } finally {
        setBusy(false);
      }
    },
    [busy, refresh, session],
  );

  return {
    game,
    connection,
    error,
    setError,
    busy: busy || pendingOffline > 0,
    pendingOffline,
    command,
    refresh,
  };
}
